/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import { pick } from 'lodash';
import type { InitialProps } from '@opengeoweb/core';
import { getPluginState } from '@opengeoweb/plugins';
import { soundingsSelectors } from '@opengeoweb/soundings';
import type { SoundingsRootState } from '@opengeoweb/soundings';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  genericSelectors,
  mapSelectors,
  selectorMemoizationOptions,
  syncGroupsSelectors,
} from '@opengeoweb/store';
import { timeSeriesSelectors } from '@opengeoweb/time-series';
import { publicWarningsSelectors } from '@opengeoweb/warnings';
import { initialState } from './reducer';
import type { AppStore } from '../types';
import type {
  WorkspaceState,
  WorkspacePreset,
  WorkspacePresetForBE,
} from './types';
import type {
  WorkspaceSupportedComponentTypes,
  ViewPreset,
} from '../viewPresets/types';
import { emptyViewPreset } from '../viewPresets/utils';

export const getWorkspaceState = (store: AppStore): WorkspaceState =>
  store?.workspace ?? initialState;

/**
 * Gets component types from the views used in the workspace
 *
 * @param {object} store store: object - Store object
 * @returns {WorkspaceViewType.componentType[]} returnType: WorkspaceViewType.componentType[]
 */
export const getComponentTypesFromActiveViews = createSelector(
  [(state: AppStore): WorkspaceState | undefined => state.workspace],
  (workspace) => {
    const viewIds = workspace?.views.allIds; // Assuming you have all view IDs here
    const componentTypes = viewIds?.map(
      (viewId) => workspace?.views?.byId?.[viewId]?.componentType ?? '',
    );
    return componentTypes;
  },
);

/**
 * Gets mosaicNode from workspace state
 *
 * @param {object} store store: object - Store object
 * @returns {MosaicNode<string>} returnType: MosaicNode<string>
 */
export const getMosaicNode = createSelector(
  getWorkspaceState,
  (state) => state?.mosaicNode,
  selectorMemoizationOptions,
);

export const getWorkspaceId = (store: AppStore): string | undefined =>
  getWorkspaceState(store)?.id;

/**
 * Gets view from workspace state
 *
 * @param {object} store store: object - Store object
 * @param {string} viewId viewId: string - view id
 * @returns {object} returnType: WorkspaceViewType
 */
export const getViewById = createSelector(
  getWorkspaceState,
  (store: AppStore, viewId: string) => viewId,
  (store, viewId) => {
    return store?.views?.byId?.[viewId];
  },
  selectorMemoizationOptions,
);

export const getViewsById = createSelector(
  getWorkspaceState,
  (store) => store.views.byId,
);

/**
 * Gets all view ids
 *
 * @param {object} store store: object - Store object
 * @returns {object} returnType: string[]
 */
export const getViewIds = createSelector(
  getWorkspaceState,
  (store) => store.views.allIds || [],
  selectorMemoizationOptions,
);

/**
 * Gets getShouldPreventClose from workspace state
 *
 * @param {object} store store: object - Store object
 * @param {string} viewId viewId: string - view id
 * @returns {boolean} returnType: boolean
 */
export const getShouldPreventClose = createSelector(
  getViewById,
  (store) => store?.shouldPreventClose ?? false,
  selectorMemoizationOptions,
);

export const isWorkspaceLoading = createSelector(
  getWorkspaceState,
  (store) => store?.isLoading ?? false,
  selectorMemoizationOptions,
);

export const getWorkspaceError = createSelector(
  getWorkspaceState,
  (store) => store?.error,
  selectorMemoizationOptions,
);
/**
 * Gets selected workspace title
 *
 * @param {object} store store: object - Store object
 * @returns {string} returnType: string
 */
export const getSelectedWorkspaceTitle = createSelector(
  getWorkspaceState,
  (store) => store?.title || '',
  selectorMemoizationOptions,
);

export const hasWorkspaceChanges = createSelector(
  getWorkspaceState,
  (store) => store?.hasChanges ?? false,
  selectorMemoizationOptions,
);

/**
 * Gets selected viewType
 *
 * @param {object} store store: object - Store object
 * @returns {string} returnType: string
 */
export const getViewType = createSelector(
  getWorkspaceState,
  (store) => store?.viewType ?? '',
  selectorMemoizationOptions,
);

/**
 * Gets selected workspace scope
 *
 * @param {object} store store: object - Store object
 * @returns {string} returnType: string
 */
export const getSelectedWorkspaceScope = createSelector(
  getWorkspaceState,
  (store) => store?.scope ?? 'system',
  selectorMemoizationOptions,
);

type WorkspaceData = WorkspacePresetForBE &
  Pick<WorkspacePreset, 'isTimeScrollingEnabled'>;

/**
 * Gets workspace data to send to the backend
 *
 * @param {object} store store: object - Store object
 * @returns {object} returnType: object - WorkspacePresetFromBE
 */
export const getWorkspaceData: (store: AppStore) => WorkspaceData =
  createSelector(
    getWorkspaceState,
    genericSelectors.getSynchronizationGroupStore,
    (workspaceStore, syncGroupsStore): WorkspaceData => {
      const syncGroups =
        syncGroupsStore?.groups.allIds.map((id) => {
          const { type } = syncGroupsStore.groups.byId[id];
          return { id, type };
        }) || [];

      const views = workspaceStore.views.allIds.map((mosaicNodeId) => {
        const view = workspaceStore.views.byId[mosaicNodeId];

        return {
          mosaicNodeId,
          componentType: view?.componentType ?? 'Map',
          title: view?.title ?? mosaicNodeId,
          originalViewPresetId: view?.id ?? undefined,
        };
      });

      const linkedState = pick(
        syncGroupsStore?.linkedState.links,
        workspaceStore.views.allIds,
      );

      return {
        id: workspaceStore.id,
        title: workspaceStore.title,
        syncGroups,
        views,
        viewType: workspaceStore.viewType,
        scope: workspaceStore.scope,
        abstract: workspaceStore.abstract,
        mosaicNode: workspaceStore.mosaicNode,
        isTimeScrollingEnabled:
          syncGroupsStore?.isTimeScrollingEnabled || false,
        linking: linkedState,
        labels: workspaceStore.labels,
        description: workspaceStore.description,
        updated: workspaceStore.updated,
        username: workspaceStore.username,
      };
    },
    selectorMemoizationOptions,
  ) as (store: AppStore) => WorkspaceData;

export const getViewComponentType = createSelector(
  getViewById,
  (store) =>
    (store?.componentType as WorkspaceSupportedComponentTypes | undefined) ??
    'Map',
  selectorMemoizationOptions,
);

export const getViewPresetToSendToBackend = createSelector(
  getViewById,
  getViewComponentType,
  mapSelectors.getMapPreset,
  syncGroupsSelectors.getAllTargetGroupsForSource,
  timeSeriesSelectors.getTimeSeriesPreset,
  publicWarningsSelectors.getWarningsPresetById,
  mapSelectors.getMultiMapInitialProps,
  mapSelectors.getTimeSliderInitialProps,
  soundingsSelectors.getSoundingsPreset,
  getPluginState,
  (store: SoundingsRootState, panelId: string) =>
    soundingsSelectors.getSoundingsPreset(store, panelId),
  (store: CoreAppStore) => store,
  (panelId: string) => panelId,
  (_store: CoreAppStore, _panelId: string, keepAllDims = false) => keepAllDims,
  (
    workspaceView,
    viewComponentType,
    mapPreset,
    syncGroupsIds,
    timeSeriesPreset,
    warningsPreset,
    multiMapPreset,
    timeSliderPreset,
    soundingsPreset,
    pluginState,
    store,
    panelId,
    keepAllDims,
  ): Omit<ViewPreset, 'title'> & { title?: string } => {
    const title = workspaceView?.title;
    const scope = workspaceView?.scope ?? 'system';

    const defaultInitialProps = { mapPreset, syncGroupsIds };

    const harmonieProps = mapSelectors.getHarmonieInitialProps(
      store,
      panelId,
      workspaceView?.initialProps,
      syncGroupsIds,
    );

    const initialPropsByComponentType: Record<
      string,
      InitialProps | undefined
    > = {
      Map: defaultInitialProps,
      TimeSeries: { ...timeSeriesPreset, syncGroupsIds },
      WarningList: warningsPreset,
      WarningsTimeline: warningsPreset,
      MultiMap: multiMapPreset ?? defaultInitialProps,
      HarmonieTempAndPrecipPreset: harmonieProps?.layers
        ? { layers: harmonieProps.layers }
        : undefined,
      TimeSlider: timeSliderPreset,
      Soundings: { ...soundingsPreset, syncGroupsIds },
      Plugin: {
        ...workspaceView?.initialProps,
        syncGroupsIds,
        ...(pluginState && { pluginState }),
      },
    };

    return {
      ...emptyViewPreset(viewComponentType),
      id: workspaceView?.id,
      scope,
      title,
      initialProps:
        initialPropsByComponentType[viewComponentType] ?? defaultInitialProps,
    };
  },
  selectorMemoizationOptions,
);
